import React, { useEffect, useMemo, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
// import moment from "moment";
import { Form, Formik, FormikProps } from "formik";
import { useSelector, useDispatch } from "react-redux";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import Table from "../../../ui-components/table/table";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "ui-components/button/button";
import {
    TerminalLogFilterModel,
    TerminalLogFilterSchema,
    TerminalLogItem
} from "../../../app/data/terminal-details/models";
import "./terminalChangelog.scss";
import { PathParams, TerminalSummaryPath } from "../route";
import { getTerminalsLog, terminalChangelogSelector } from "../../../slices/terminal-summary/changelogSlice";
import { useParams } from "react-router-dom";
import TerminalChangelogState from "../../../slices/terminal-summary/ChangelogState";

const initialValues: TerminalLogFilterModel
    = {
    dateFrom: undefined,
    dateTo: undefined,
};


const TerminalChangelog: React.FC<{}> = () => {
    const dispatch = useDispatch();
    const params = useParams() as PathParams;
    const terminalChangelogState: TerminalChangelogState = useSelector(terminalChangelogSelector);
    const filterFormRef = useRef<FormikProps<TerminalLogFilterModel>>(null);
    const [filter, setFilter] = useState<TerminalLogFilterModel>(initialValues);
    const terminalNumber = useMemo(() => params[TerminalSummaryPath.number], [params]);

    const columnHelper = createColumnHelper<TerminalLogItem>();

    const columns = [
        columnHelper.accessor("changedAt", {
            id: "changedAt",
            header: "Timestamp",
            cell: (cellProps) => (<>
                {cellProps.getValue() && (
                    <div className="xgs-terminal-summary-changelog__date">
                        <div>{cellProps.getValue().toUiDateFormat()}</div> <div className="xgs-terminal-summary-changelog__gray-value">{cellProps.getValue().toUiTimeShortFormat()}</div>
                    </div>
                )}
            </>
            ),
            size: 180,
            minSize: 150,
        }),
        columnHelper.accessor("modifiedBy", {
            id: "modifiedBy",
            header: "User ID",
            size: 200,
            minSize: 150,
        }),
        columnHelper.accessor("changeDescription", {
            id: "changeDescription",
            header: "Change Description",
            size: 400,
            minSize: 200,
        }),
    ];

    const diffsAfterSplitting = useMemo(() => {
        let diffs: {
            changedAt: string;
            modifiedBy: string;
            changeDescription: string;
        }[] = [];

        if (!terminalChangelogState.terminalLog) return [];

        terminalChangelogState.terminalLog.forEach((item) => {
            const lines = item.changeDescription.split("\n").filter(line => line.trim() !== "");

            lines.forEach((line) => {
                diffs.push({
                    changedAt: item.changedAt,
                    modifiedBy: item.modifiedBy,
                    changeDescription: line.replaceAll("-->", "→"),
                });
            });
        });
        return diffs;
    }, [terminalChangelogState.terminalLog]);

    const onListInfiniteScroll = () => {
        if (terminalChangelogState.terminalLogRequest.last) return;
        const nextPage = terminalChangelogState.terminalLogRequest.page + 1;
        dispatch(getTerminalsLog(terminalNumber, filter, nextPage));
    };

    const onFilterSubmit = (data: TerminalLogFilterModel) => {

        const formattedFilter: TerminalLogFilterModel = {
            dateFrom: data.dateFrom ? data.dateFrom.toApiDateFormat() : undefined,
            dateTo: data.dateTo ? data.dateTo.toApiDateFormat() : undefined,
        };

        setFilter(formattedFilter);
        dispatch(getTerminalsLog(terminalNumber, formattedFilter));
    };
    useEffect(() => {
        if (terminalNumber) {
            dispatch(getTerminalsLog(terminalNumber, filter))
        }
        return () => {
            dispatch(getTerminalsLog(terminalNumber, filter))
        }
    }, [dispatch, terminalNumber, filter])
    return (
        <div className="xgs-terminal-summary-changelog">

            <div className="xgs-terminal-summary-changelog__header">Change Log</div>
            <div className="xgs-terminal-summary-changelog__grid">

                <Formik
                    initialValues={initialValues}
                    onSubmit={onFilterSubmit}
                    validationSchema={TerminalLogFilterSchema}
                    innerRef={filterFormRef}
                >
                    {(props: FormikProps<TerminalLogFilterModel>) => (
                        <Form>
                            <div className="xgs-terminal-summary-changelog__controls__search">
                                <LabeledDateRangeInput
                                    label="Filter by Date Range:"
                                    labelMode={LabelModes.column}
                                    start={props.values.dateFrom}
                                    end={props.values.dateTo}
                                    maxDate={new Date()}
                                    onStartChange={(v) => {
                                        props.setFieldValue("dateFrom", v || undefined);
                                        props.submitForm();
                                    }}
                                    onEndChange={(v) => {
                                        props.setFieldValue("dateTo", v || undefined);
                                        props.submitForm();
                                    }}
                                />
                                <Button
                                    type="submit"
                                    theme={ButtonThemes.blue}
                                    className="xgs-terminal-summary-changelog__controls__button"
                                >
                                    Search
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            <div className="xgs-terminal-summary-changelog__table-wrapper">

                {!terminalChangelogState?.requestFailed && terminalChangelogState?.terminalLog && (
                    <Table
                        isLoading={terminalChangelogState.requestStarted && (terminalChangelogState.requestCreator === "GET_TERMINALS_LOG")}
                        columns={columns}
                        data={diffsAfterSplitting}
                        rowHeight={48}
                        infiniteScroll={true}
                        noResultsText="No changelog entries found for the selected date range."
                        infiniteScrollLoading={terminalChangelogState.fetchPortionStarted && terminalChangelogState.requestCreator === "GET_PICKUPS_LOG_PORTION"}
                        infiniteScrollHasNext={!terminalChangelogState.terminalLogRequest.last}
                        onInfiniteScroll={onListInfiniteScroll}
                    />

                )}
            </div>

            {terminalChangelogState?.requestFailed && (
                <XGSErrorMessage className="xgs-terminal-log__error">{terminalChangelogState.requestError || "Error while obtaining the log"}</XGSErrorMessage>
            )}
        </div>

    );
};
export default TerminalChangelog;
